import type { Animation, Layer, SlideDirection } from '../types';

export interface RenderResult {
  visible: boolean;
  /** Style applied to the wrapping element (transform, opacity). */
  wrapperStyle: Partial<CSSStyleDeclaration>;
  /** Style applied to the inner content element (e.g., clip-path for typing). */
  innerStyle: Partial<CSSStyleDeclaration>;
  /** Style applied to a sibling overlay element on top of the inner content
   *  (color / gradient overlay). `display: none` when no overlay is active. */
  overlayStyle: Partial<CSSStyleDeclaration>;
  /** Strike progress 0..1 — only meaningful for text layers using `strike` enter. */
  strikeProgress: number;
}

const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number): number => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

function slideOffset(dir: SlideDirection | undefined, distance = 140): { x: number; y: number } {
  switch (dir) {
    case 'left': return { x: -distance, y: 0 };
    case 'right': return { x: distance, y: 0 };
    case 'top': return { x: 0, y: -distance };
    case 'bottom': return { x: 0, y: distance };
    default: return { x: -distance, y: 0 };
  }
}

interface Mutable {
  tx: number;
  ty: number;
  sx: number;
  sy: number;
  opacity: number;
  clipPercent: number;
  strike: number;
}

function applyEnter(anim: Animation | undefined, progress: number, m: Mutable): void {
  if (!anim || progress >= 1) return;
  const p = easeOut(progress);
  switch (anim.type) {
    case 'fade':
      m.opacity *= p;
      return;
    case 'slide': {
      const { x, y } = slideOffset(anim.from);
      m.tx += x * (1 - p);
      m.ty += y * (1 - p);
      m.opacity *= p;
      return;
    }
    case 'pop': {
      const ep = easeOutBack(progress);
      m.sx *= ep;
      m.sy *= ep;
      m.opacity *= Math.min(1, progress * 2);
      return;
    }
    case 'typing':
      m.clipPercent = p * 100;
      return;
    case 'strike':
      m.strike = progress;
      return;
  }
}

function applyExit(anim: Animation | undefined, progress: number, m: Mutable): void {
  if (!anim || progress <= 0) return;
  const p = easeOut(progress);
  switch (anim.type) {
    case 'slide': {
      const { x, y } = slideOffset(anim.to);
      m.tx += x * p;
      m.ty += y * p;
      m.opacity *= 1 - p;
      return;
    }
    case 'fade':
    default:
      m.opacity *= 1 - p;
  }
}

function getProgress(layer: Layer, t: number): { enter: number; exit: number } {
  const enterDur = layer.enter?.duration ?? 0;
  const exitDur = layer.exit?.duration ?? 0;

  const enter = enterDur > 0 ? clamp01((t - layer.start) / enterDur) : 1;

  let exit = 0;
  if (exitDur > 0) {
    const exitStart = layer.end - exitDur;
    if (t >= exitStart) exit = clamp01((t - exitStart) / exitDur);
  }
  return { enter, exit };
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

function escapeUrl(src: string): string {
  return src.replace(/"/g, '%22');
}

export function renderLayer(layer: Layer, t: number): RenderResult {
  if (!layer.visible || t < layer.start || t > layer.end) {
    return {
      visible: false,
      wrapperStyle: {},
      innerStyle: {},
      overlayStyle: { display: 'none' },
      strikeProgress: 0,
    };
  }

  const m: Mutable = {
    tx: 0,
    ty: 0,
    sx: 1,
    sy: 1,
    opacity: layer.transform.opacity,
    clipPercent: 100,
    strike: 1,
  };

  const { enter, exit } = getProgress(layer, t);
  applyEnter(layer.enter, enter, m);
  applyExit(layer.exit, exit, m);

  const wrapperStyle: Partial<CSSStyleDeclaration> = {
    opacity: String(clamp01(m.opacity)),
    transform:
      `translate(-50%, -50%) ` +
      `translate(${m.tx}px, ${m.ty}px) ` +
      `rotate(${layer.transform.rotate}deg) ` +
      `scale(${m.sx}, ${m.sy})`,
  };

  // ─── Pure-CSS visual effects (LayerEffects) ───
  // filter is composed onto the wrapper, box-shadow / blend-mode on the
  // wrapper, border-radius / border on the inner so the surface gets
  // clipped rather than the bounding box.
  //
  // CRITICAL: every effect property is written on EVERY render, even when
  // its value is "off". Otherwise the stale style from the previous frame
  // sticks to the DOM (CSS is not a re-renderer — Object.assign only adds).
  // That's why dragging blur back to 0 used to leave the last non-zero
  // blur on screen.
  const fx = layer.effects ?? {};
  const innerStyle: Partial<CSSStyleDeclaration> = {};
  const filters: string[] = [];
  if (typeof fx.blur === 'number' && fx.blur > 0) filters.push(`blur(${fx.blur}px)`);
  if (typeof fx.brightness === 'number' && fx.brightness !== 1) filters.push(`brightness(${fx.brightness})`);
  if (typeof fx.contrast === 'number' && fx.contrast !== 1) filters.push(`contrast(${fx.contrast})`);
  if (typeof fx.saturate === 'number' && fx.saturate !== 1) filters.push(`saturate(${fx.saturate})`);
  if (typeof fx.grayscale === 'number' && fx.grayscale > 0) filters.push(`grayscale(${fx.grayscale})`);
  if (typeof fx.sepia === 'number' && fx.sepia > 0) filters.push(`sepia(${fx.sepia})`);
  if (typeof fx.hueRotate === 'number' && fx.hueRotate !== 0) filters.push(`hue-rotate(${fx.hueRotate}deg)`);
  if (typeof fx.invert === 'number' && fx.invert > 0) filters.push(`invert(${fx.invert})`);
  if (fx.dropShadow) {
    const d = fx.dropShadow;
    filters.push(`drop-shadow(${d.x}px ${d.y}px ${d.blur}px ${d.color})`);
  }
  // Always write — empty string clears any previous filter chain.
  wrapperStyle.filter = filters.length ? filters.join(' ') : '';

  if (fx.shadow) {
    const s = fx.shadow;
    const inset = s.inset ? 'inset ' : '';
    wrapperStyle.boxShadow = `${inset}${s.x}px ${s.y}px ${s.blur}px ${s.spread}px ${s.color}`;
  } else {
    wrapperStyle.boxShadow = '';
  }
  wrapperStyle.mixBlendMode = fx.blendMode && fx.blendMode !== 'normal' ? fx.blendMode : '';
  if (typeof fx.borderRadius === 'number' && fx.borderRadius > 0) {
    innerStyle.borderRadius = `${fx.borderRadius}px`;
    innerStyle.overflow = 'hidden';
  } else {
    innerStyle.borderRadius = '';
    innerStyle.overflow = '';
  }
  if (fx.border && fx.border.width > 0) {
    innerStyle.border = `${fx.border.width}px ${fx.border.style} ${fx.border.color}`;
    innerStyle.boxSizing = 'border-box';
  } else {
    innerStyle.border = '';
  }
  if (layer.enter?.type === 'typing' && enter < 1) {
    innerStyle.clipPath = `inset(0 ${100 - m.clipPercent}% 0 0)`;
  }

  // ─── Overlay (color or gradient on top of the surface) ───
  const overlayStyle: Partial<CSSStyleDeclaration> = {};
  let overlayBg = '';
  let overlayBlend = 'normal';
  let overlayOpacity = 1;
  if (fx.colorOverlay) {
    overlayBg = fx.colorOverlay.color;
    overlayBlend = fx.colorOverlay.blendMode ?? 'normal';
    overlayOpacity = fx.colorOverlay.opacity ?? 1;
  }
  if (fx.gradientOverlay && fx.gradientOverlay.stops?.length >= 2) {
    const g = fx.gradientOverlay;
    const stops = g.stops
      .slice()
      .sort((a, b) => a.pos - b.pos)
      .map((s) => `${s.color} ${Math.round(s.pos * 100)}%`)
      .join(', ');
    const css = g.kind === 'linear'
      ? `linear-gradient(${g.angle ?? 90}deg, ${stops})`
      : `radial-gradient(circle at center, ${stops})`;
    // Solid color overlay below + gradient on top (when both set).
    overlayBg = overlayBg ? `${css}, ${overlayBg}` : css;
    overlayBlend = g.blendMode ?? overlayBlend;
    overlayOpacity = g.opacity ?? overlayOpacity;
  }
  if (overlayBg) {
    overlayStyle.display = 'block';
    overlayStyle.background = overlayBg;
    overlayStyle.mixBlendMode = overlayBlend;
    overlayStyle.opacity = String(overlayOpacity);
    // Inherit the layer's corner radius so the overlay clips with it.
    if (typeof fx.borderRadius === 'number' && fx.borderRadius > 0) {
      overlayStyle.borderRadius = `${fx.borderRadius}px`;
    } else {
      overlayStyle.borderRadius = '';
    }
    // For PNG/SVG images, mask the overlay to the source's alpha so the
    // wash only paints over the visible pixels — Photoshop's "color overlay"
    // semantics. Falls back to the rectangular overlay for other layer kinds.
    if (layer.kind === 'image') {
      const url = layer.src;
      const fit = layer.fit === 'cover' ? 'cover' : 'contain';
      overlayStyle.maskImage = `url("${escapeUrl(url)}")`;
      (overlayStyle as Record<string, string>).webkitMaskImage = `url("${escapeUrl(url)}")`;
      overlayStyle.maskMode = 'alpha';
      (overlayStyle as Record<string, string>).webkitMaskMode = 'alpha';
      overlayStyle.maskSize = fit;
      (overlayStyle as Record<string, string>).webkitMaskSize = fit;
      overlayStyle.maskPosition = 'center';
      (overlayStyle as Record<string, string>).webkitMaskPosition = 'center';
      overlayStyle.maskRepeat = 'no-repeat';
      (overlayStyle as Record<string, string>).webkitMaskRepeat = 'no-repeat';
    } else {
      // Clear any previously-set mask so toggling between layer kinds works.
      overlayStyle.maskImage = '';
      (overlayStyle as Record<string, string>).webkitMaskImage = '';
    }
  } else {
    overlayStyle.display = 'none';
  }

  return {
    visible: m.opacity > 0.001,
    wrapperStyle,
    innerStyle,
    overlayStyle,
    strikeProgress: layer.enter?.type === 'strike' ? m.strike : 1,
  };
}
